/**
 * lazyRoute — a code-split route that survives the app being deployed
 * underneath it.
 *
 * THE FAILURE THIS EXISTS FOR. Every screen is a separate chunk with a
 * content hash in its filename, and a deploy replaces the whole set. A tab
 * that was already open still holds the OLD index, so the moment somebody
 * navigates, the browser asks for a chunk that no longer exists on the
 * server. `React.lazy` gets nothing back and reads `.default` off it, and
 * the screen dies with "Cannot read properties of undefined (reading
 * 'default')" — a message about the app's own plumbing, on a screen that is
 * perfectly fine, for a user who did nothing wrong.
 *
 * THE FIX IS A RELOAD, and that is worth doing rather than asking for. The
 * cause is deterministic — the tab is out of date — and so is the remedy:
 * fetch the new index and its new chunk names. Anything else leaves somebody
 * staring at an error card whose only useful button is the one this does
 * automatically.
 *
 * ONCE PER CHUNK, PER TAB. A reload loop is far worse than the error it
 * replaces: it makes the app unusable and hides the real fault. So the
 * attempt is remembered, and a second failure of the same chunk is allowed
 * to surface as an error — by then a reload has already been tried and did
 * not help, which means it was never a stale chunk.
 *
 * A SUCCESSFUL LOAD FORGETS. Otherwise one stale-chunk reload months ago
 * would spend this screen's only retry for the life of the tab.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

/** What a chunk load needs from the world, so the rule can be tested without
 * a browser. */
export interface IChunkEnv {
  /** Has a reload already been spent on this chunk in this tab? */
  reloadedAlready(key: string): boolean;
  markReloaded(key: string): void;
  clearReloaded(key: string): void;
  /** Fetch the new index. Never returns — the page is going away. */
  reload(): void;
}

/**
 * A module namespace that actually carries a component.
 *
 * `import()` resolving to undefined is the case this whole module exists
 * for, so it is checked rather than assumed — and a module that arrives
 * WITHOUT a default is the same failure wearing different clothes, since
 * React.lazy will read `.default` off it either way.
 */
function usable<T>(mod: { default?: T } | undefined | null): mod is { default: T } {
  return !!mod && mod.default !== undefined && mod.default !== null;
}

/**
 * Load a route's chunk, reloading the page once if it does not arrive.
 *
 * Returns a promise that NEVER settles when it decides to reload: the page
 * is on its way out, and resolving or rejecting would flash an error card
 * that is about to be thrown away anyway.
 */
export async function loadChunk<T>(
  key: string,
  load: () => Promise<{ default?: T } | undefined>,
  env: IChunkEnv,
): Promise<{ default: T }> {
  let mod: { default?: T } | undefined;
  let failure: unknown;
  try {
    mod = await load();
  } catch (e) {
    failure = e;
  }
  if (usable<T>(mod)) {
    env.clearReloaded(key);
    return mod;
  }
  if (!env.reloadedAlready(key)) {
    env.markReloaded(key);
    env.reload();
    // Deliberately never settles — see above.
    return new Promise<never>(() => undefined);
  }
  // A reload has already been tried and did not help, so this is not a stale
  // chunk. Say what was being loaded: "reading 'default'" names nothing.
  throw failure instanceof Error
    ? failure
    : new Error(`Could not load the code for this screen (${key}). Reloading did not help.`);
}

/** sessionStorage, per TAB — a stale chunk is a property of this tab's age,
 * and every access is guarded because a browser set to block site data throws
 * on the accessor itself. */
const STORE_PREFIX = "chunk-reload:";
export const browserChunkEnv: IChunkEnv = {
  reloadedAlready(key) {
    try { return sessionStorage.getItem(STORE_PREFIX + key) !== null; } catch { return false; }
  },
  markReloaded(key) {
    try { sessionStorage.setItem(STORE_PREFIX + key, "1"); } catch { /* nothing to do */ }
  },
  clearReloaded(key) {
    try { sessionStorage.removeItem(STORE_PREFIX + key); } catch { /* nothing to do */ }
  },
  reload() {
    window.location.reload();
  },
};

/**
 * `lazy()`, with the stale-chunk recovery above.
 *
 * `key` is the import specifier, which is stable across deploys — the chunk's
 * hashed FILE name is not, and keying on that would spend a fresh retry on
 * every release.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors
// React's own lazy() signature; narrowing it here would reject every route
// component that takes props.
export function lazyRoute<T extends ComponentType<any>>(
  key: string,
  load: () => Promise<{ default: T }>,
  env: IChunkEnv = browserChunkEnv,
): LazyExoticComponent<T> {
  return lazy(() => loadChunk<T>(key, load, env));
}
